import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import { useUser } from '../contexts/UserContext.jsx';
import { useToastMessage } from '../contexts/ToastMessageContext.jsx';
import { changeLanguage } from '../i18n/index.js';
import {
  FileCheck,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  Sparkles,
  Search,
  ChevronRight,
} from 'lucide-react';

/** 승인 시 앱에서 RCC 생성·다운로드 허용 기간 (frontend `rccIssuance.js`와 동일하게 유지) — 3개월(90일) */
const RCC_APPROVAL_VALIDITY_MS = 90 * 24 * 60 * 60 * 1000;
/** H-3: 어드민 페이지네이션 페이지당 크기 */
const PAGE_SIZE = 50;
/** H-1: 표준화된 거절 사유 enum — 사용자 앱(frontend MyPage.json)의 키와 1:1 매칭 */
const REJECTION_CODES = [
  'INCOMPLETE_DOCS',
  'IDENTITY_MISMATCH',
  'NO_REMITTANCE',
  'DUPLICATE_REQUEST',
  'OTHER',
];

const localeForI18n = (lng) => {
  if (lng === 'en') return 'en-US';
  if (lng === 'my') return 'my-MM';
  return 'ko-KR';
};

const AdminRccApplicationsPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const { showToastMessage } = useToastMessage();
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionId, setActionId] = useState(null);
  // H-1: enum 우선, 메모는 보조 자유 텍스트
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, userId: null });
  const [rejectCodeDraft, setRejectCodeDraft] = useState('OTHER');
  const [rejectMemoDraft, setRejectMemoDraft] = useState('');
  // H-3: 페이지네이션 커서
  const [lastDocCursor, setLastDocCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const formatDate = useCallback(
    (v) => {
      if (!v) return '—';
      const d = v?.toDate ? v.toDate() : new Date(v);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString(localeForI18n(i18n.language));
    },
    [i18n.language]
  );

  const statusLabel = useMemo(
    () => ({
      pending: t('rccPage.statusPending'),
      approved: t('rccPage.statusApproved'),
      rejected: t('rccPage.statusRejected'),
    }),
    [t]
  );

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const rows = useMemo(() => {
    const byStatus =
      filter === 'all' ? allRows : allRows.filter((r) => String(r.status || '').toLowerCase() === filter);
    if (!normalizedSearch) return byStatus;
    return byStatus.filter((r) => {
      const haystack = [r.userId, r.userEmail, r.userDisplayName, r.rejectionReason, r.rejectionCode]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [allRows, filter, normalizedSearch]);

  const pendingCount = useMemo(
    () => allRows.filter((r) => String(r.status || '').toLowerCase() === 'pending').length,
    [allRows]
  );

  /** H-3: 페이지네이션 — 처음 로드 또는 새로고침 */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const base = collection(db, 'artifacts', appId, 'rcc_applications');
      const q = query(base, orderBy('requestedAt', 'desc'), limit(PAGE_SIZE));
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllRows(list);
      setLastDocCursor(snap.docs.length ? snap.docs[snap.docs.length - 1] : null);
      setHasMore(snap.docs.length >= PAGE_SIZE);
    } catch (e) {
      console.error('RCC 신청 목록 로드 실패:', e?.code || e?.message || 'unknown');
      showToastMessage(t('rccPage.toastLoadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToastMessage, t]);

  /** H-3: 페이지네이션 — 더 불러오기 */
  const loadMore = useCallback(async () => {
    if (!lastDocCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const base = collection(db, 'artifacts', appId, 'rcc_applications');
      const q = query(base, orderBy('requestedAt', 'desc'), startAfter(lastDocCursor), limit(PAGE_SIZE));
      const snap = await getDocs(q);
      const additional = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllRows((prev) => [...prev, ...additional]);
      setLastDocCursor(snap.docs.length ? snap.docs[snap.docs.length - 1] : lastDocCursor);
      setHasMore(snap.docs.length >= PAGE_SIZE);
    } catch (e) {
      console.error('RCC 추가 로드 실패:', e?.code || e?.message || 'unknown');
      showToastMessage(t('rccPage.toastLoadError'), 'error');
    } finally {
      setLoadingMore(false);
    }
  }, [lastDocCursor, loadingMore, showToastMessage, t]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (
    id,
    status,
    { rejectionCode = '', rejectionMemo = '', applicantUserId = null } = {}
  ) => {
    // M-8: 자기 자신의 RCC 신청은 승인/거절 불가
    if (applicantUserId && user?.uid && applicantUserId === user.uid) {
      showToastMessage(t('rccPage.toastSelfActionBlocked'), 'error');
      return;
    }
    setActionId(id);
    try {
      const ref = doc(db, 'artifacts', appId, 'rcc_applications', id);
      if (status === 'approved') {
        await updateDoc(ref, {
          status: 'approved',
          approvedAt: serverTimestamp(),
          approvedBy: user?.email || user?.uid || null,
          validUntil: Timestamp.fromMillis(Date.now() + RCC_APPROVAL_VALIDITY_MS),
        });
        showToastMessage(t('rccPage.toastApproved'), 'success');
        if (applicantUserId) {
          try {
            await addDoc(collection(db, 'artifacts', appId, 'users', applicantUserId, 'in_app_messages'), {
              title: t('rccPage.inAppTitle'),
              body: t('rccPage.inAppBody'),
              type: 'success',
              category: 'rcc_approved',
              isRead: false,
              isClicked: false,
              createdAt: serverTimestamp(),
              action: t('rccPage.inAppAction'),
              actionUrl: '/my-page?openRcc=1',
              rccApplicationId: id,
              // H-2: 자동 소멸 해제 — 사용자가 직접 확인할 때까지 유지
              duration: 0,
              persistent: true,
            });
          } catch (notifyErr) {
            console.error('RCC 승인 인앱 알림 실패:', notifyErr?.code || notifyErr?.message);
          }
        }
      } else {
        // H-1: enum 기반 사유 + 자유 메모(보조)
        const safeCode = REJECTION_CODES.includes(rejectionCode) ? rejectionCode : 'OTHER';
        const memo = typeof rejectionMemo === 'string' ? rejectionMemo.trim() : '';
        await updateDoc(ref, {
          status: 'rejected',
          rejectedAt: serverTimestamp(),
          rejectedBy: user?.email || user?.uid || null,
          rejectionCode: safeCode,
          // 사용자에게는 enum→i18n 번역이 우선 노출. memo 는 운영자 추적용
          rejectionReason: memo || null,
        });
        showToastMessage(t('rccPage.toastRejected'), 'success');
      }
      await load();
    } catch (e) {
      console.error('RCC 상태 변경 실패:', e?.code || e?.message || 'unknown');
      showToastMessage(t('rccPage.toastActionError'), 'error');
    } finally {
      setActionId(null);
    }
  };

  const openRejectModal = (id, userId) => {
    setRejectCodeDraft('OTHER');
    setRejectMemoDraft('');
    setRejectModal({ open: true, id, userId });
  };

  const confirmReject = async () => {
    const id = rejectModal.id;
    if (!id) return;
    await setStatus(id, 'rejected', {
      rejectionCode: rejectCodeDraft,
      rejectionMemo: rejectMemoDraft,
      applicantUserId: rejectModal.userId,
    });
    setRejectModal({ open: false, id: null, userId: null });
    setRejectCodeDraft('OTHER');
    setRejectMemoDraft('');
  };

  const filters = useMemo(
    () => [
      { id: 'pending', label: t('rccPage.filterPending'), Icon: Clock },
      { id: 'approved', label: t('rccPage.filterApproved'), Icon: CheckCircle },
      { id: 'rejected', label: t('rccPage.filterRejected'), Icon: XCircle },
      { id: 'all', label: t('rccPage.filterAll'), Icon: FileCheck },
    ],
    [t]
  );

  const langButtons = useMemo(
    () => [
      { code: 'ko', label: t('rccPage.langKo') },
      { code: 'en', label: t('rccPage.langEn') },
      { code: 'my', label: t('rccPage.langMy') },
    ],
    [t]
  );

  return (
    <div className="min-h-full p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Hero header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative overflow-hidden rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950/80 p-6 sm:p-8 shadow-2xl shadow-indigo-950/40"
        >
          <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-indigo-600/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 left-1/4 h-48 w-48 rounded-full bg-violet-600/15 blur-3xl" />

          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/30">
                <FileCheck className="h-7 w-7 text-white" strokeWidth={2} />
              </div>
              <div>
                <div className="mb-1 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-indigo-200/90">
                  <Sparkles className="h-3.5 w-3.5 text-amber-300/90" />
                  {t('rccPage.badge')}
                </div>
                <h1 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">
                  {t('rccPage.title')}
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">
                  {t('rccPage.subtitle')}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-100">
                    <Clock className="h-3.5 w-3.5" />
                    {t('rccPage.filterPending')}: {pendingCount}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                    <Shield className="h-3.5 w-3.5 text-emerald-400/80" />
                    Firestore · rcc_applications
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center lg:flex-col lg:items-end">
              <div className="flex rounded-xl border border-slate-600/80 bg-slate-950/50 p-1 shadow-inner">
                {langButtons.map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => changeLanguage(code)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                      i18n.language?.startsWith(code)
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => load()}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-600 bg-slate-800/80 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-700 disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                {t('rccPage.refresh')}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Filters + Search (H-3) */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {filters.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setFilter(id)}
                className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-all ${
                  filter === id
                    ? 'border-indigo-400/60 bg-indigo-600 text-white shadow-lg shadow-indigo-900/40'
                    : 'border-slate-600/80 bg-slate-800/60 text-slate-300 hover:border-slate-500 hover:bg-slate-800'
                }`}
              >
                <Icon size={16} strokeWidth={2} />
                {label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('rccPage.searchPlaceholder')}
              className="w-full rounded-xl border border-slate-600 bg-slate-900/60 pl-9 pr-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            />
          </div>
        </div>

        {/* Table card */}
        <div className="overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-900/40 shadow-xl backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/80 bg-slate-800/90 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <th className="whitespace-nowrap px-4 py-3">{t('rccPage.colRequestedAt')}</th>
                  <th className="whitespace-nowrap px-4 py-3">{t('rccPage.colStatus')}</th>
                  <th className="whitespace-nowrap px-4 py-3">{t('rccPage.colUserId')}</th>
                  <th className="whitespace-nowrap px-4 py-3">{t('rccPage.colEmail')}</th>
                  <th className="whitespace-nowrap px-4 py-3">{t('rccPage.colName')}</th>
                  <th className="whitespace-nowrap px-4 py-3">{t('rccPage.colValidUntil')}</th>
                  <th className="whitespace-nowrap px-4 py-3">{t('rccPage.colNotes')}</th>
                  <th className="whitespace-nowrap px-4 py-3">{t('rccPage.colActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/60 text-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-16 text-center text-slate-500">
                      {t('rccPage.loading')}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-16 text-center text-slate-500">
                      {t('rccPage.empty')}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="transition-colors hover:bg-slate-800/50">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                        {formatDate(r.requestedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-lg px-2.5 py-1 text-xs font-bold ${
                            r.status === 'approved'
                              ? 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30'
                              : r.status === 'rejected'
                                ? 'bg-red-500/15 text-red-300 ring-1 ring-red-500/30'
                                : 'bg-amber-500/15 text-amber-200 ring-1 ring-amber-500/30'
                          }`}
                        >
                          {statusLabel[r.status] || r.status || '—'}
                        </span>
                      </td>
                      <td className="max-w-[140px] break-all px-4 py-3 font-mono text-xs text-slate-400">
                        {r.userId}
                      </td>
                      <td className="px-4 py-3 text-slate-300">{r.userEmail || '—'}</td>
                      <td className="px-4 py-3">{r.userDisplayName || '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-400">
                        {r.status === 'approved' ? formatDate(r.validUntil) : '—'}
                      </td>
                      <td className="max-w-[200px] break-words px-4 py-3 text-xs text-slate-400">
                        {r.status === 'rejected' && r.rejectionReason ? r.rejectionReason : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {r.status === 'pending' ? (
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={actionId === r.id || (user?.uid && r.userId === user.uid) /* M-8 */}
                              onClick={() => setStatus(r.id, 'approved', { applicantUserId: r.userId })}
                              title={user?.uid && r.userId === user.uid ? t('rccPage.toastSelfActionBlocked') : ''}
                              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-md transition hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {t('rccPage.approve')}
                            </button>
                            <button
                              type="button"
                              disabled={actionId === r.id || (user?.uid && r.userId === user.uid)}
                              onClick={() => openRejectModal(r.id, r.userId)}
                              title={user?.uid && r.userId === user.uid ? t('rccPage.toastSelfActionBlocked') : ''}
                              className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-bold text-slate-200 transition hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {t('rccPage.reject')}
                            </button>
                          </div>
                        ) : r.status === 'approved' ? (
                          <span className="text-xs text-slate-500">
                            {t('rccPage.approvedMeta')} {formatDate(r.approvedAt)}
                            {r.approvedBy ? ` · ${r.approvedBy}` : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-500">
                            {t('rccPage.rejectedMeta')} {formatDate(r.rejectedAt)}
                            {r.rejectedBy ? ` · ${r.rejectedBy}` : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* H-3: 페이지네이션 더 불러오기 */}
          {hasMore && !loading && (
            <div className="border-t border-slate-700/80 bg-slate-900/60 px-4 py-3 flex justify-center">
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => void loadMore()}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-700 disabled:opacity-50"
              >
                {loadingMore ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    {t('rccPage.loadingMore')}
                  </>
                ) : (
                  <>
                    <ChevronRight size={14} />
                    {t('rccPage.loadMore')}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {rejectModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rcc-reject-title"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              className="w-full max-w-md rounded-2xl border border-slate-600/80 bg-slate-900 p-6 shadow-2xl"
            >
              <h2 id="rcc-reject-title" className="text-lg font-bold text-white">
                {t('rccPage.rejectModalTitle')}
              </h2>
              <p className="mt-2 text-sm text-slate-400">{t('rccPage.rejectModalHint')}</p>

              {/* H-1: 표준화된 거절 사유 선택 — 사용자 앱에서 다국어 자동 매핑 */}
              <div className="mt-4">
                <label className="block text-xs font-semibold text-slate-300 mb-2">
                  {t('rccPage.rejectReasonCodeLabel')}
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {REJECTION_CODES.map((code) => (
                    <label
                      key={code}
                      className={`flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer transition ${
                        rejectCodeDraft === code
                          ? 'border-red-400 bg-red-500/10'
                          : 'border-slate-600 bg-slate-800/40 hover:bg-slate-800'
                      }`}
                    >
                      <input
                        type="radio"
                        name="rcc-reject-code"
                        value={code}
                        checked={rejectCodeDraft === code}
                        onChange={() => setRejectCodeDraft(code)}
                        className="mt-1 accent-red-500"
                      />
                      <span className="text-sm text-slate-100">
                        {t(`rccPage.rejectReasonOptions.${code}`)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-semibold text-slate-300 mb-2">
                  {t('rccPage.rejectMemoLabel')}
                </label>
                <textarea
                  value={rejectMemoDraft}
                  onChange={(e) => setRejectMemoDraft(e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-slate-600 bg-slate-800/80 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                  placeholder={t('rccPage.rejectPlaceholder')}
                />
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setRejectModal({ open: false, id: null, userId: null });
                    setRejectCodeDraft('OTHER');
                    setRejectMemoDraft('');
                  }}
                  className="rounded-xl border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:bg-slate-800"
                >
                  {t('rccPage.cancel')}
                </button>
                <button
                  type="button"
                  disabled={actionId === rejectModal.id}
                  onClick={() => confirmReject()}
                  className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-red-500 disabled:opacity-50"
                >
                  {t('rccPage.confirmReject')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminRccApplicationsPage;
